import React, { useState, useEffect } from 'react';

const StoreIssueReportPrint = ({ id = "19" }) => {
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null); 

  // Default data structure for fallback
  const defaultReportData = {
    reqNo: "",
    fromStore: "",
    refNo: "",
    date: "",
    toStore: "",
    status: "",
    department: "",
    employee: "",
    remarks: "",
    items: [],
    grandTotal: "₹0.00"
  };

  // Fetch data from API
  useEffect(() => {
    const fetchReportData = async () => {
      if (!id) {
        setReportData(defaultReportData);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const response = await fetch(`https://nissi-india.com/api/storesIssue_rptPrint/${id}`);
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const res = await response.json();
        const data = res.data?.[0] || {};
        console.log("Fetched data:", data);
        
        // Check if API call was successful
        if (!data.success && data.success !== undefined) {
          throw new Error('API returned unsuccessful response');
        }

        // Format date function
        const formatDate = (dateString) => {
          if (!dateString) return new Date().toLocaleDateString('en-GB');
          const date = new Date(dateString);
          return date.toLocaleDateString('en-GB');
        };

        // Transform API data to match our component structure
        const transformedData = {
          reqNo: data.fld_storesissueno || "",
          branch: data.fld_BranchName || "",
          intno: data.fld_Indentref || "",
          date: formatDate(data.fld_date) || "",
          project: data.Store || "",
          remarks: data.fld_remarks || "",
          items: [
            {
              sNo: 1,
              itemCode: data.fld_itemcode || "",
              description: data.fld_itemname || "",
              department: data.fld_departmentname || "",
              unit: data.fld_uomname || "",
              qty: data.fld_qty || "",
              unitPrice: data.fld_itemPrice
                ? `₹${parseFloat(data.fld_itemPrice).toFixed(2)}`
                : "₹0.00",
              totalAmount:
                data.fld_itemPrice && data.fld_qty
                  ? `₹${(parseFloat(data.fld_itemPrice) * parseFloat(data.fld_qty)).toFixed(2)}`
                  : "₹0.00",
              vehicleName: data.fld_vehichlename || "",
              vehicleNo: data.fld_vehicle_regno || "",
              supplier: data.Supplier || "",
            },
          ],
          grandTotal:
            data.fld_itemPrice && data.fld_qty
              ? `₹${(parseFloat(data.fld_itemPrice) * parseFloat(data.fld_qty)).toFixed(2)}`
              : "₹0.00",
        };

        setReportData(transformedData);
        setError(null);
      } catch (err) {
        console.error('Error fetching report data:', err);
        setError(err.message);
        setReportData(defaultReportData);
      } finally {
        setLoading(false);
      }
    };

    fetchReportData();
  }, [id]);

  // Calculate grand total from all items
  const calculateGrandTotal = () => {
    if (!reportData?.items) return "₹0.00";
    
    const total = reportData.items.reduce((sum, item) => {
      const amount = item.totalAmount?.replace('₹', '') || '0';
      return sum + parseFloat(amount);
    }, 0);
    
    return `₹${total.toFixed(2)}`;
  };

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto p-6 bg-white font-sans text-sm">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading report data...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!reportData) {
    return (
      <div className="max-w-4xl mx-auto p-6 bg-white font-sans text-sm">
        <div className="flex items-center justify-center h-64">
          <div className="text-center text-red-600">
            <p className="text-lg font-semibold mb-2">Error Loading Report</p>
            <p>{error || "Failed to load report data"}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6 bg-white font-sans text-sm">
      {/* API Error Warning (if using fallback data) */}
      {error && (
        <div className="mb-4 p-3 bg-yellow-100 border border-yellow-400 text-yellow-700 rounded no-print">
          <p className="text-sm">
            <strong>Note:</strong> Could not fetch data from API ({error}). Showing template.
          </p>
        </div>
      )}

      {/* Outer Border */}
      <div className="border-4 border-black p-4 print:border-black">
        
        {/* Header Section */}
        <div className="border-2 border-black mb-4 print:border-black">
          <div className="flex items-start p-4">
            {/* Logo Section */}
            <div className="w-32 h-20 border-2 border-black mr-6 flex items-center justify-center bg-white">
              <div className="text-center">
                <div className="w-12 h-8 bg-black mx-auto mb-1 flex items-center justify-center">
                  <span className="text-white text-xs font-bold">LOGO</span>
                </div>
                <div className="text-xs font-bold">NISSI INFOTECH</div>
                <div className="text-xs">PVT LTD</div>
              </div>
            </div>
            
            {/* Company Info */}
            <div className="flex-1 text-center">
              <h1 className="text-xl font-bold mb-1">Nissi Infotech</h1>
              <p className="text-sm mb-1">NISSI</p>
              <p className="text-sm font-bold mb-1">CIVIL CONSTRUCTION</p>
              <p className="text-xs">
                3/42-12, Nagappa colony, kavundampai,644003,Tamil Nadu,India,<br/>
                Phone No :9003744644 infraconsoft-implementation@nissiinfotech.in
              </p>
            </div>
          </div>
        </div>

        {/* Report Title */}
        <div className="text-center border-b-2 border-black pb-2 mb-4 print:border-black">
          <h2 className="text-lg font-bold underline">Store Issue Report</h2>
        </div>

        {/* Report Details */}
        <div className="grid grid-cols-3 gap-x-8 mb-4 text-sm">
          <div className="space-y-1">
            <div className="flex">
              <span className="font-bold w-24">Store Issue No</span>
              <span className="mr-2">:</span>
              <span>{reportData.reqNo}</span>
            </div>
            <div className="flex">
              <span className="font-bold w-24">Date</span>
              <span className="mr-2">:</span>
              <span>{reportData.date}</span>
            </div>
            <div className="flex">
              <span className="font-bold w-24">Remarks</span>
              <span className="mr-2">:</span>
              <span>{reportData.remarks}</span>
            </div>
          </div>
          
          <div className="space-y-1">
            <div className="flex">
              <span className="font-bold w-24">Project</span>
              <span className="mr-2">:</span>
              <span>{reportData.project}</span>
            </div>
            <div className="flex">
              <span className="font-bold w-24">Branch</span>
              <span className="mr-2">:</span>
              <span>{reportData.branch}</span>
            </div>
            <div className="flex">
              <span className="font-bold w-24">Indent No</span>
              <span className="mr-2">:</span>
              <span>{reportData.intno}</span>
            </div>
          </div>
        </div>

        {/* Items Table */}
        <div className="border-2 border-black mb-4 print:border-black">
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-white print:bg-white">
                <th className="border border-black px-2 py-2 text-center w-12 print:border-black">S.No</th>
                <th className="border border-black px-2 py-2 text-center w-24 print:border-black">Item Code</th>
                <th className="border border-black px-2 py-2 text-center print:border-black">Description</th>
                <th className="border border-black px-2 py-2 text-center print:border-black">Department</th>
                <th className="border border-black px-2 py-2 text-center w-16 print:border-black">UOM</th>
                <th className="border border-black px-2 py-2 text-center w-16 print:border-black">Qty</th>
                <th className="border border-black px-2 py-2 text-center w-20 print:border-black">Price</th>
                <th className="border border-black px-2 py-2 text-center w-24 print:border-black">Total</th>
                <th className="border border-black px-2 py-2 text-center w-20 print:border-black">Vehicle Name</th>
                <th className="border border-black px-2 py-2 text-center print:border-black">Veh.No.</th>
                <th className="border border-black px-2 py-2 text-center print:border-black">Supplier</th>
              </tr>
            </thead>
            <tbody>
              {reportData.items.map((item) => (
                <tr key={item.sNo}>
                  <td className="border border-black px-2 py-2 text-center print:border-black">{item.sNo}</td>
                  <td className="border border-black px-2 py-2 text-center print:border-black">{item.itemCode}</td>
                  <td className="border border-black px-2 py-2 print:border-black">{item.description}</td>
                  <td className="border border-black px-2 py-2 text-center print:border-black">{item.department}</td>
                  <td className="border border-black px-2 py-2 text-center print:border-black">{item.unit}</td>
                  <td className="border border-black px-2 py-2 text-center print:border-black">{item.qty}</td>
                  <td className="border border-black px-2 py-2 text-right print:border-black">{item.unitPrice}</td>
                  <td className="border border-black px-2 py-2 text-right print:border-black">{item.totalAmount}</td>
                  <td className="border border-black px-2 py-2 text-center print:border-black">{item.vehicleName}</td>
                  <td className="border border-black px-2 py-2 text-center print:border-black">{item.vehicleNo}</td>
                  <td className="border border-black px-2 py-2 text-center print:border-black">{item.supplier}</td>
                </tr>
              ))}
              
              {/* Empty rows to maintain table height */}
              {reportData.items.length < 10 && [...Array(10 - reportData.items.length)].map((_, index) => (
                <tr key={`empty-${index}`} className="h-6">
                  <td className="border border-black px-2 py-2 print:border-black"></td>
                  <td className="border border-black px-2 py-2 print:border-black"></td>
                  <td className="border border-black px-2 py-2 print:border-black"></td>
                  <td className="border border-black px-2 py-2 print:border-black"></td>
                  <td className="border border-black px-2 py-2 print:border-black"></td>
                  <td className="border border-black px-2 py-2 print:border-black"></td>
                  <td className="border border-black px-2 py-2 print:border-black"></td>
                  <td className="border border-black px-2 py-2 print:border-black"></td>
                  <td className="border border-black px-2 py-2 print:border-black"></td>
                  <td className="border border-black px-2 py-2 print:border-black"></td>
                  <td className="border border-black px-2 py-2 print:border-black"></td>
                </tr>
              ))}

              {/* Grand Total Row - Fixed colspan */}
              <tr>
                <td className="border border-black px-2 py-2 print:border-black" colSpan="7"></td>
                <td className="border border-black px-2 py-2 text-center font-bold print:border-black">Grand Total:</td>
                <td className="border border-black px-2 py-2 text-right font-bold print:border-black">{calculateGrandTotal()}</td>
                <td className="border border-black px-2 py-2 print:border-black"></td>
                <td className="border border-black px-2 py-2 print:border-black"></td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Footer */}
        <div className="border-2 border-black print:border-black">
          <div className="p-2 text-right text-xs">
            <span className="font-bold">Page 1 of 1</span>
          </div>
          <div className="border-t-2 border-black p-2 text-xs print:border-black">
            Report generated by ERP-Software - www.infraconsoft.com owned by <span className="font-bold">www.nissiinfotech.com</span>
          </div>
        </div>

      </div>

      {/* Print Button */}
      <div className="mt-6 text-center no-print">
        <button
          onClick={() => window.print()}
          className="bg-blue-600 text-white px-6 py-2 rounded hover:bg-blue-700 transition-colors mr-4"
        >
          Print Report
        </button>
        {id && (
          <button
            onClick={() => window.location.reload()}
            className="bg-gray-600 text-white px-6 py-2 rounded hover:bg-gray-700 transition-colors"
          >
            Refresh Data
          </button>
        )}
      </div>

      {/* Print Styles */}
      <style jsx>{`
        @media print {
          .no-print {
            display: none !important;
          }
          body {
            margin: 0;
            padding: 10mm;
            background: white !important;
          }
          * {
            color: black !important;
            background: white !important;
            border-color: black !important;
          }
          .print\\:border-black {
            border-color: black !important;
          }
          .print\\:bg-white {
            background-color: white !important;
          }
        }
      `}</style>
    </div>
  );
};

export default StoreIssueReportPrint;